import type { WithBreakpoints } from '@cc-shopping/ui/theme';
import { Box, type SxProps } from '@mui/material';

type AllowedSizeUnits = `${number}px` | `${number}rem`;

export type RatingProps = {
  additionalStyles?: SxProps;
  testid?: string;
  size?: WithBreakpoints<AllowedSizeUnits>;
  value?: number;
  gap?: WithBreakpoints<AllowedSizeUnits>;
  color?: string;
};

const emptyAndFilledCommonStyles = {
  content: '""',
  position: 'absolute',
  inset: 0,
  WebkitMaskRepeat: 'repeat-x',
  maskRepeat: 'repeat-x',
  WebkitMaskSize:
    'calc(var(--star-rating-size) + var(--star-rating-gap) - 1px) var(--star-rating-size)',
  maskSize: 'calc(var(--star-rating-size) + var(--star-rating-gap) - 1px) var(--star-rating-size)',
  WebkitMaskPosition: 'left center',
  maskPosition: 'left center',
};

export function Rating({
  additionalStyles,
  color = '#008254',
  gap = '0.15rem',
  testid,
  size = '24px',
  value,
}: RatingProps) {
  if (!value) return null;
  return (
    <Box
      component="span"
      data-testid={testid}
      aria-label={`${value} out of 5`}
      sx={{
        '--value': value,
        '--star-fill': color,
        '--star-rating-gap': gap,
        '--star-rating-size': size,
        '--star-rating-max': 5,
        '--star-empty': '#ccc',
        '--pct': 'calc(((var(--value) * 100%) / var(--star-rating-max)) + 1px)',
        position: 'relative',
        display: 'inline-block',
        inlineSize: `calc(
          var(--star-rating-size) * var(--star-rating-max) +
            (max(var(--star-rating-max) - 1, 0) * var(--star-rating-gap))
        )`,
        blockSize: 'var(--star-rating-size)',
        lineHeight: 1,
        fontSize: 'var(--star-rating-size)',
        fontFamily: 'system-ui, sans-serif',
        '&:before': {
          background: 'var(--star-empty)',
          WebkitMaskImage: 'var(--empty-star-svg)',
          maskImage: 'var(--empty-star-svg)',
          ...emptyAndFilledCommonStyles,
        },
        '&:after': {
          background: 'var(--star-fill)',
          inlineSize: 'var(--pct)',
          overflow: 'hidden',
          WebkitMaskImage: 'var(--star-svg)',
          maskImage: 'var(--star-svg)',
          ...emptyAndFilledCommonStyles,
        },
        '@supports not (mask-image: var(--star-svg))': {
          '&:before': {
            content: '"★★★★★"',
            color: 'var(--star-empty)',
            letterSpacing: 'var(--star-rating-gap)',
          },
          '&:after': {
            content: '"★★★★★"',
            color: 'var(--star-fill)',
            letterSpacing: 'var(--star-rating-gap)',
            position: 'absolute',
            inset: 0,
            inlineSize: 'var(--pct)',
            overflow: 'hidden',
          },
        },
        '@media (forced-colors: active)': {
          '&:before': {
            background: 'CanvasText',
          },
          '&:after': {
            background: 'Highlight',
          },
        },
        ...additionalStyles,
      }}
    />
  );
}
